import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IncomingOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [formData, setFormData] = useState({
    order_number: '',
    customer_id: '',
    customer_name: '',
    article_id: '',
    article_name: '',
    quantity: 0,
    delivery_date: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchOrders();
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [customersRes, articlesRes] = await Promise.all([
        axios.get(`${API}/master-data?entity_type=customer`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/master-data?entity_type=article`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCustomers(customersRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/incoming-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = { ...formData, quantity: parseFloat(formData.quantity) };
      if (editOrder) {
        payload.id = editOrder.id;
        await axios.put(`${API}/incoming-orders/${editOrder.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/incoming-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditOrder(null);
      setFormData({ order_number: '', customer_id: '', customer_name: '', article_id: '', article_name: '', quantity: 0, delivery_date: '', status: 'pending' });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/incoming-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Incoming Orders</h1>
              <p className="text-sm text-gray-600">Customer orders received</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditOrder(null); setFormData({ order_number: '', customer_id: '', customer_name: '', article_id: '', article_name: '', quantity: 0, delivery_date: '', status: 'pending' }); }} data-testid="add-order-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Order
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="order-dialog">
              <DialogHeader>
                <DialogTitle>{editOrder ? 'Edit Order' : 'Create Order'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Order Number</Label>
                  <Input value={formData.order_number} onChange={(e) => setFormData({ ...formData, order_number: e.target.value })} required data-testid="order-number-input" />
                </div>
                <div className="space-y-2">
                  <Label>Customer</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.customer_id}
                    onChange={(e) => {
                      const customer = customers.find(c => c.id === e.target.value);
                      setFormData({ ...formData, customer_id: e.target.value, customer_name: customer?.name || '' });
                    }} required data-testid="customer-select">
                    <option value="">Select Customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Article</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.article_id}
                    onChange={(e) => {
                      const article = articles.find(a => a.id === e.target.value);
                      setFormData({ ...formData, article_id: e.target.value, article_name: article?.name || '' });
                    }} required data-testid="article-select">
                    <option value="">Select Article</option>
                    {articles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input type="number" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} required data-testid="quantity-input" />
                </div>
                <div className="space-y-2">
                  <Label>Delivery Date</Label>
                  <Input type="date" value={formData.delivery_date} onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} data-testid="delivery-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="status-select">
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-order">{editOrder ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order.id} data-testid={`order-row-${order.id}`}>
                  <td className="px-6 py-4">{order.order_number}</td>
                  <td className="px-6 py-4">{order.customer_name}</td>
                  <td className="px-6 py-4">{order.article_name}</td>
                  <td className="px-6 py-4">{order.quantity}</td>
                  <td className="px-6 py-4"><span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{order.status}</span></td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => { setEditOrder(order); setFormData({ ...order, delivery_date: order.delivery_date?.split('T')[0] || '' }); setOpen(true); }} data-testid={`edit-order-${order.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(order.id)} data-testid={`delete-order-${order.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && <div className="text-center py-12" data-testid="no-orders"><p className="text-gray-500">No orders found</p></div>}
      </main>
    </div>
  );
}
